/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

var LogLevel;
(function (LogLevel) {
    LogLevel[LogLevel["silly"] = 0] = "silly";
    LogLevel[LogLevel["trace"] = 1] = "trace";
    LogLevel[LogLevel["debug"] = 2] = "debug";
    LogLevel[LogLevel["info"] = 3] = "info";
    LogLevel[LogLevel["warn"] = 4] = "warn";
    LogLevel[LogLevel["error"] = 5] = "error";
    LogLevel[LogLevel["fatal"] = 6] = "fatal";
    LogLevel[LogLevel["log"] = 7] = "log";
})(LogLevel || (LogLevel = {}));

var REDACTED_KEYWORDS = [
    'apikey',
    'accesstoken',
    'Authorization',
    'secret',
].map(function (k) { return k.toLowerCase(); });
var REDACTED_VALUE = '[REDACTED]';
var redact = function (info, options) {
    if (options === void 0) { options = { disableRedaction: false }; }
    if (options.disableRedaction)
        return info;
    var redactionKeyWords = options.keyWords
        ? REDACTED_KEYWORDS.concat(options.keyWords)
        : REDACTED_KEYWORDS;
    if (typeof info !== 'object') {
        return info;
    }
    else if (Array.isArray(info)) {
        return info.map(function (val) { return redact(val, options); });
    }
    else {
        return Object.keys(info).reduce(function (acc, key) {
            if (isKeyWordMatch(key, redactionKeyWords)) {
                acc[key] = options.redactedValue || REDACTED_VALUE;
            }
            else {
                acc[key] = info[key] ? redact(info[key], options) : info[key];
            }
            return acc;
        }, {});
    }
};
var isKeyWordMatch = function (key, keywords) {
    return keywords.map(function (k) { return k.toLowerCase(); }).includes(key.toLowerCase());
};

var InternalLogger = (function () {
    function InternalLogger(actualLogger, loggerOptions) {
        if (loggerOptions === void 0) { loggerOptions = {}; }
        this.actualLogger = actualLogger;
        this.loggerOptions = loggerOptions;
    }
    InternalLogger.prototype.redact = function (info) {
        return info ? redact(info, this.loggerOptions.redaction) : info;
    };
    InternalLogger.prototype.child = function (settings) {
        return this.actualLogger.child(settings, this.loggerOptions);
    };
    InternalLogger.prototype.silly = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.silly, message, additionalDetails);
    };
    InternalLogger.prototype.trace = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.trace, message, additionalDetails);
    };
    InternalLogger.prototype.debug = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.debug, message, additionalDetails);
    };
    InternalLogger.prototype.info = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.info, message, additionalDetails);
    };
    InternalLogger.prototype.warn = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.warn, message, additionalDetails);
    };
    InternalLogger.prototype.error = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.error, message, additionalDetails);
    };
    InternalLogger.prototype.fatal = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.fatal, message, additionalDetails);
    };
    InternalLogger.prototype.log = function (message, additionalDetails) {
        this.emitLogMessage(LogLevel.log, message, additionalDetails);
    };
    InternalLogger.prototype.emitLogMessage = function (logLevelId, message, additionalDetails) {
        var logLevelType = LogLevel[logLevelId];
        if (this.actualLogger && this.loggerOptions.desiredLogLevel <= logLevelId) {
            this.actualLogger[logLevelType](message, this.redact(additionalDetails));
        }
    };
    return InternalLogger;
}());

var ConsoleLoggerAdapter = (function () {
    function ConsoleLoggerAdapter(mergeObject) {
        this.mergeObject = mergeObject;
    }
    ConsoleLoggerAdapter.prototype.merge = function (additionalDetails) {
        return __assign(__assign({}, this.mergeObject), additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.child = function (mergeObject, loggerOptions) {
        return new InternalLogger(new ConsoleLoggerAdapter(this.merge(mergeObject)), loggerOptions);
    };
    ConsoleLoggerAdapter.prototype.info = function (message, additionalDetails) {
        this.emitLogMessage('info', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.warn = function (message, additionalDetails) {
        this.emitLogMessage('warn', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.error = function (message, additionalDetails) {
        this.emitLogMessage('error', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.fatal = function (message, additionalDetails) {
        this.emitLogMessage('error', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.silly = function (message, additionalDetails) {
        this.emitLogMessage('info', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.trace = function (message, additionalDetails) {
        this.emitLogMessage('info', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.debug = function (message, additionalDetails) {
        this.emitLogMessage('debug', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.log = function (message, additionalDetails) {
        this.emitLogMessage('log', message, additionalDetails);
    };
    ConsoleLoggerAdapter.prototype.emitLogMessage = function (logType, message, additionalDetails) {
        additionalDetails || this.mergeObject
            ? console[logType](message, this.merge(additionalDetails))
            : console[logType](message);
    };
    return ConsoleLoggerAdapter;
}());

var NullLogger = (function () {
    function NullLogger() {
    }
    NullLogger.prototype.silly = function (message, additionalDetails) { };
    NullLogger.prototype.trace = function (message, additionalDetails) { };
    NullLogger.prototype.debug = function (message, additionalDetails) { };
    NullLogger.prototype.info = function (message, additionalDetails) { };
    NullLogger.prototype.warn = function (message, additionalDetails) { };
    NullLogger.prototype.error = function (message, additionalDetails) { };
    NullLogger.prototype.fatal = function (message, additionalDetails) { };
    NullLogger.prototype.log = function (message, additionalDetails) { };
    NullLogger.prototype.child = function (mergeObject) {
        return new NullLogger();
    };
    return NullLogger;
}());

var isProduction = process.env.NODE_ENV === 'production';
var prefix = 'Invariant failed';
function invariant(condition, message) {
    if (condition) {
        return;
    }
    if (isProduction) {
        throw new Error(prefix);
    }
    var provided = typeof message === 'function' ? message() : message;
    var value = provided ? "".concat(prefix, ": ").concat(provided) : prefix;
    throw new Error(value);
}

var internalLogger = new InternalLogger(new NullLogger());
var initialized = false;
var warned = false;
function initializeLogger(logger, loggerOptions) {
    invariant(!initialized, 'Attempted to reinitialize logger');
    internalLogger = new InternalLogger(logger, loggerOptions);
    invariant(internalLogger, 'Failed to create a logger');
    initialized = true;
    return internalLogger;
}
function getLogger(metadata) {
    if (!initialized && !warned && process.env.NODE_ENV !== 'test') {
        warned = true;
        console.warn('internalLogger was not initialized');
    }
    if (metadata)
        return internalLogger.child(metadata);
    return internalLogger;
}
function getLibLoggingMetadata(name, version, additionalMetadata) {
    return __assign({ lib: {
            name: name,
            version: version,
        } }, additionalMetadata);
}
function getMfeLoggingMetadata(name, version, gatsbyPhase, environment, additionalMetadata) {
    return __assign({ mfe: {
            name: name,
            version: version,
            gatsbyPhase: gatsbyPhase,
            environment: environment,
        } }, additionalMetadata);
}
function initConsoleLogger(settings, loggerOptions) {
    var loggingAdapter = new ConsoleLoggerAdapter(settings);
    var logger = initializeLogger(loggingAdapter, loggerOptions);
    logger.debug('Console logger initialized');
}
function initCustomLogger(loggingAdapter, loggerOptions) {
    var logger = initializeLogger(loggingAdapter, loggerOptions);
    logger.debug('Custom logger initialized');
}

var Dispenser = (function () {
    function Dispenser(createInstance) {
        this.createInstance = createInstance;
    }
    Dispenser.prototype.getInstance = function () {
        if (!this.instance) {
            this.instance = this.createInstance();
        }
        return this.instance;
    };
    return Dispenser;
}());

var loggerDispenser = new Dispenser(function () { return getLogger(); });

export { ConsoleLoggerAdapter, Dispenser, InternalLogger, LogLevel, NullLogger, getLibLoggingMetadata, getLogger, getMfeLoggingMetadata, initConsoleLogger, initCustomLogger, initializeLogger, loggerDispenser };
